// src/integrations/player-core/index.ts
import coreAnalytics from "@nrk/core-analytics";
import cuid2 from "cuid";

// src/integrations/player-core/app-insights-log-handler.ts
import { LogLevel } from "@nrk/player-core/utils";
import { PlaybackSessionError } from "@nrk/player-core/types";
var AppInsightsLogHandler = class {
  constructor(appTracker) {
    this.appTracker = appTracker;
  }
  log(namespace, level, message, ...messages) {
    if (namespace === "Metric") {
      this.appTracker.trackMetric(message, messages[0]);
      return;
    }
    if (namespace === "AppTracker") {
      return;
    }
    if (level === LogLevel.Error) {
      if (message instanceof PlaybackSessionError || messages.some((m) => m instanceof PlaybackSessionError)) {
        return;
      }
      this.appTracker.trackEvent("log-error", { namespace, message: `${message}`, log: [message, ...messages] });
    }
  }
};

// src/tracking-apis/app-tracker.ts
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { abortable as abortable2 } from "@nrk/abortable";
import { getLogger } from "@nrk/player-core/utils";

// src/utils/before-unload.ts
import { abortable, addEventListener } from "@nrk/abortable";
function beforeUnload(callback) {
  const controller = abortable([
    addEventListener(window, "beforeunload", () => {
      controller.abort();
      callback();
    }),
    addEventListener(window, "pagehide", () => {
      controller.abort();
      callback();
    })
  ]);
  return () => controller.abort();
}

// src/tracking-apis/app-tracker.ts
var logger = getLogger("AppTracker");
var AppTracker = class {
  destroyController = new AbortController();
  appInsights;
  commonProps;
  propsCallbacks = [];
  constructor(appInsights, props) {
    this.appInsights = appInsights;
    this.commonProps = props;
    abortable2(
      beforeUnload(() => this.appInsights.onunloadFlush()),
      this.destroyController.signal
    );
  }
  updateInstanceProps(properties) {
    Object.assign(this.commonProps, properties);
  }
  addPropsCallback(callback) {
    if (!this.propsCallbacks.includes(callback)) {
      this.propsCallbacks.push(callback);
    }
  }
  removePropsCallback(callback) {
    this.propsCallbacks = this.propsCallbacks.filter((cb) => cb !== callback);
  }
  trackEvent(name2, ...props) {
    const properties = this.mergeProps(props);
    logger.debug("Sending event:", name2, properties);
    this.appInsights.trackEvent({ name: name2, properties });
  }
  trackQoSEvent(...props) {
    const mergedProperties = this.mergeProps(props);
    delete mergedProperties.playback;
    delete mergedProperties.mediaInfo;
    delete mergedProperties.displayModes;
    const eventName = "nativePlayerQos";
    mergedProperties.properties.applicationName = mergedProperties.applicationName;
    mergedProperties.properties.applicationVersion = mergedProperties.applicationVersion;
    logger.debug("Sending event:", eventName, mergedProperties);
    this.appInsights.trackEvent({ name: eventName, properties: mergedProperties });
  }
  trackMetric(name2, value, ...props) {
    const properties = this.mergeProps(props);
    logger.debug("Sending metric:", name2, value, properties);
    this.appInsights.trackMetric({ name: name2, average: value, properties });
  }
  trackException(exception, fatal, ...props) {
    const severityLevel = fatal ? SeverityLevel.Error : SeverityLevel.Warning;
    const properties = this.mergeProps(props);
    logger.debug("Sending exception:", name, properties);
    this.appInsights.trackException({ exception, severityLevel, properties });
  }
  startTrackEvent(name2) {
    this.appInsights.startTrackEvent(name2);
  }
  stopTrackEvent(name2, ...props) {
    const properties = this.mergeProps(props);
    this.appInsights.stopTrackEvent(name2, properties);
    logger.debug("Sent timed event:", name2, properties);
  }
  flush() {
    this.appInsights.flush();
  }
  destroy() {
    this.commonProps = {};
    this.propsCallbacks = [];
    this.destroyController.abort();
  }
  mergeProps(propSets) {
    return filterEmptyProps(Object.assign({}, this.commonProps, ...this.propsCallbacks.map((cb) => cb()), ...propSets));
  }
};
function filterEmptyProps(obj) {
  return Object.keys(obj).reduce((o, k) => {
    const v = obj[k];
    if (v !== void 0) {
      o[k] = v;
    }
    return o;
  }, {});
}

// src/integrations/player-core/index.ts
import { CoreEvent as CoreEvent10, version as playerCoreVersion } from "@nrk/player-core/types";

// src/tracking-apis/ga-sender.ts
import { getLogger as getLogger2 } from "@nrk/player-core/utils";
var logger2 = getLogger2("GASender");
var DimensionMap = {
  Id: "dimension1",
  Title: "dimension2",
  Year: "dimension3",
  Month: "dimension4",
  Day: "dimension5",
  ReferrerDomain: "dimension6",
  Referrer: "dimension7",
  UserIdHit: "dimension8",
  UserIdSession: "dimension9",
  SourceId: "dimension10",
  Category: "dimension23",
  Autoplay: "dimension24",
  MediaType: "dimension25",
  MediaCategory: "dimension26",
  MimeType: "dimension27",
  AdapterType: "dimension28",
  ErrorType: "dimension31",
  ErrorReason: "dimension30"
};
var MetricMap = {
  Started: "metric21",
  PlayedTime: "metric22",
  TotalPlayedTime: "metric23",
  Stopped: "metric24",
  TimeBehindLive: "metric25"
};
var GASender = class {
  coreTracker;
  commonFields;
  constructor(coreTracker, appFields, mediaFields) {
    this.coreTracker = coreTracker;
    this.commonFields = appFields;
    this.updateFields(mediaFields || {});
  }
  updateFields(mediaFields) {
    if ("fields" in mediaFields && mediaFields.fields) {
      Object.assign(this.commonFields, mediaFields.fields);
    }
    if ("userId" in mediaFields) {
      this.coreTracker("context", { userId: mediaFields.userId });
    }
    if ("sourceId" in mediaFields) {
      this.commonFields[DimensionMap.SourceId] = mediaFields.sourceId;
    }
    if ("programId" in mediaFields) {
      this.commonFields[DimensionMap.Id] = mediaFields.programId;
    }
    if ("autoplay" in mediaFields) {
      this.commonFields[DimensionMap.Autoplay] = mediaFields.autoplay ? "Yes" : "No";
    }
    if ("mimeType" in mediaFields) {
      this.commonFields[DimensionMap.MimeType] = mediaFields.mimeType;
    }
    if ("adapterType" in mediaFields) {
      this.commonFields[DimensionMap.AdapterType] = mediaFields.adapterType;
    }
  }
  sendEvent(hitFields) {
    const fields = filterFields({
      ...this.commonFields,
      ...hitFields
    });
    logger2.debug("Sending:", fields);
    this.coreTracker("event", { ga: fields });
  }
};
function filterFields(fields) {
  const result = {};
  const keys = Object.keys(fields);
  for (let i = 0; i < keys.length; ++i) {
    const key = keys[i];
    const value = fields[key];
    if (typeof value === "string") {
      if (value.length > 0) {
        result[key] = value;
      }
    } else if (typeof value === "number") {
      if (Number.isFinite(value)) {
        result[key] = value;
      }
    } else if (value !== void 0 && value !== null) {
      result[key] = value;
    }
  }
  return result;
}

// src/tracking-apis/ga-tracker.ts
var Quantile = /* @__PURE__ */ ((Quantile2) => {
  Quantile2["Quantile25"] = "media-playback-25";
  Quantile2["Quantile50"] = "media-playback-50";
  Quantile2["Quantile75"] = "media-playback-75";
  return Quantile2;
})(Quantile || {});
var GATracker = class extends GASender {
  sendStartedEvent(timePlayed) {
    this.sendEvent({
      eventCategory: "media-playback",
      eventAction: "media-playback-started",
      [MetricMap.PlayedTime]: timePlayed,
      [MetricMap.TotalPlayedTime]: timePlayed,
      [MetricMap.Started]: 1
    });
  }
  sendQuantileEvent(quantile, timePlayed) {
    this.sendEvent({
      eventCategory: "media-playback",
      eventAction: quantile,
      [MetricMap.PlayedTime]: timePlayed.range,
      [MetricMap.TotalPlayedTime]: timePlayed.total
    });
  }
  sendCompletedEvent(timePlayed) {
    this.sendEvent({
      eventCategory: "media-playback",
      eventAction: "media-playback-completed",
      [MetricMap.PlayedTime]: timePlayed.range,
      [MetricMap.TotalPlayedTime]: timePlayed.total,
      [MetricMap.Stopped]: 1
    });
  }
  sendProgressEvent(values) {
    this.sendEvent({
      eventCategory: "media-playback",
      eventAction: "media-playback-progress",
      [MetricMap.PlayedTime]: values.range,
      [MetricMap.TotalPlayedTime]: values.total,
      [MetricMap.TimeBehindLive]: values.timeBehindLive
    });
  }
  sendContentConsumedEvent(percentConsumed) {
    this.sendEvent({
      eventCategory: "content-consumed",
      eventAction: "percentConsumed",
      eventValue: percentConsumed,
      nonInteraction: true
    });
  }
  sendAccumulatedSecondsEvent(seconds) {
    this.sendEvent({
      eventCategory: "media-playback",
      eventAction: "media-playback-accumulated",
      eventLabel: `${seconds}-seconds`
    });
  }
  sendAccumulatedCompleteEvent() {
    this.sendEvent({
      eventCategory: "media-playback",
      eventAction: "media-playback-accumulated",
      eventLabel: "completed-stream"
    });
  }
};

// src/tracking-apis/progress-tracker.ts
var ProgressTracker = class {
  programId;
  playbackStartTime = 0;
  finalized = false;
  trackingCallback;
  constructor(programId, playbackStartTime, callback) {
    this.programId = programId;
    this.playbackStartTime = playbackStartTime;
    this.trackingCallback = callback;
  }
  reportPlaybackProgress(playbackStartTime, timePlayed) {
    if (!this.finalized) {
      this.playbackStartTime = playbackStartTime;
      this.trackingCallback({ playbackStartTime, timePlayed, programId: this.programId });
    }
  }
  finalizeProgressReporting(timePlayed) {
    if (timePlayed) {
      this.trackingCallback({ playbackStartTime: this.playbackStartTime, timePlayed, programId: this.programId });
      this.finalized = true;
    }
  }
};

// src/utils/time-range.ts
function summarizeRanges(ranges) {
  return ranges.reduce((sum, range) => sum + range.end - range.start, 0);
}
function clipRanges(ranges, targetRange) {
  if (!targetRange) {
    return ranges;
  }
  const result = [];
  for (let i = 0; i < ranges.length; ++i) {
    const range = ranges[i];
    if (range.end > targetRange.start && range.start < targetRange.end) {
      result.push({ start: Math.max(targetRange.start, range.start), end: Math.min(targetRange.end, range.end) });
    }
  }
  return result;
}
function mergeOverlappingRanges(ranges) {
  if (ranges.length === 0) {
    return [];
  }
  ranges.sort((a, b) => a.start - b.start);
  const mergedRanges = [ranges[0]];
  let currentMergeRange = 0;
  for (let i = 1; i < ranges.length; ++i) {
    const range = ranges[i];
    if (range.start > mergedRanges[currentMergeRange].end) {
      mergedRanges.push(range);
      currentMergeRange++;
    } else if (range.end > mergedRanges[currentMergeRange].end) {
      mergedRanges[currentMergeRange].end = range.end;
    }
  }
  return mergedRanges;
}

// src/utils/played-time-collector.ts
var PlayedTimeCollector = class {
  playedRanges;
  playedRangesSum;
  totalPlayingTimeListeners;
  startPosition;
  currentPosition;
  constructor() {
    this.playedRanges = [];
    this.playedRangesSum = 0;
    this.totalPlayingTimeListeners = [];
  }
  timeStarted(startPosition) {
    if (this.startPosition === void 0) {
      this.startPosition = startPosition;
    }
  }
  timeStopped(endPosition) {
    if (this.startPosition !== void 0) {
      const startPosition = this.startPosition;
      if (endPosition > startPosition) {
        const range = { start: startPosition, end: endPosition };
        const rangeDuration = endPosition - startPosition;
        this.playedRanges.push(range);
        this.playedRangesSum += rangeDuration;
        this.checkTimeListeners(this.playedRangesSum);
      }
      this.startPosition = void 0;
      this.currentPosition = void 0;
    }
  }
  timeUpdate(currentPosition) {
    if (this.startPosition !== void 0 && currentPosition >= this.startPosition) {
      this.currentPosition = currentPosition;
      const totalPlayingTime = this.playedRangesSum + currentPosition - this.startPosition;
      this.checkTimeListeners(totalPlayingTime);
    }
  }
  onceTotalPlayingTime(time, handler, context = this) {
    const totalPlayingTime = this.getTotalPlayingTime();
    if (time <= totalPlayingTime) {
      handler.call(context, totalPlayingTime);
    } else {
      this.totalPlayingTimeListeners.push({ time, handler, context });
      this.totalPlayingTimeListeners.sort((a, b) => a.time - b.time);
    }
  }
  getTotalPlayingTime() {
    if (this.startPosition !== void 0 && this.currentPosition !== void 0) {
      return this.playedRangesSum + this.currentPosition - this.startPosition;
    }
    return this.playedRangesSum;
  }
  getDurationOfMediaPlayed(targetRange) {
    const ranges = [...this.playedRanges];
    if (this.startPosition !== void 0 && this.currentPosition !== void 0) {
      ranges.push({ start: this.startPosition, end: this.currentPosition });
    }
    return summarizeRanges(mergeOverlappingRanges(clipRanges(ranges, targetRange)));
  }
  destroy() {
    this.totalPlayingTimeListeners = [];
    this.playedRanges = [];
  }
  checkTimeListeners(totalPlayingTime) {
    while (this.totalPlayingTimeListeners.length > 0) {
      if (this.totalPlayingTimeListeners[0].time <= totalPlayingTime) {
        const listener = this.totalPlayingTimeListeners.shift();
        listener?.handler.call(listener.context, totalPlayingTime);
      } else {
        break;
      }
    }
  }
};

// src/utils/site-config.ts
var siteConfigs = [
  {
    hostname: "^[^.]*\\btv\\b[^.]*\\.nrk\\.no$",
    data: {
      name: "NRK TV"
    }
  },
  {
    hostname: "^[^.]*\\bradio\\b[^.]*\\.nrk\\.no$",
    data: {
      name: "NRK Radio"
    }
  },
  {
    hostname: "^[^.]*\\btv\\b[^.]*\\.nrksuper\\.no$",
    data: {
      name: "NRK Super TV"
    }
  },
  {
    hostname: "^(.+\\.)?nrksuper\\.no$",
    data: {
      name: "NRK Super"
    }
  },
  {
    hostname: "^(.+\\.)?p3\\.no$",
    data: {
      name: "NRK P3"
    }
  },
  {
    hostname: "^(.+\\.)?nrkbeta\\.no$",
    data: {
      name: "NRK beta"
    }
  },
  {
    hostname: "^(.+\\.)?yr\\.no$",
    data: {
      name: "Yr"
    }
  },
  {
    hostname: "\\b(ark|felles\\.ds|preview|fil|kubeint|snutt|kurator)\\.nrk\\.no$",
    data: {
      environment: "internal"
    }
  },
  {
    hostname: "^static\\.nrk\\.no$",
    pathname: "^/dev/",
    data: {
      environment: "dev"
    }
  },
  {
    hostname: "(www|static)\\.nrk\\.no$",
    pathname: "^(/embed/.*|.*/video-embed(\\.html)?)$",
    data: {
      isVideoEmbed: true
    }
  },
  {
    hostname: "\\.nrk\\.no$",
    data: {
      name: "NRK.no",
      options: {
        applicationInsights: {
          samplingPercentage: 10
        }
      }
    }
  },
  {
    hostname: "^localhost$",
    data: {
      environment: "test"
    }
  },
  {
    hostname: "\\.nrk\\.cloud$",
    data: {
      environment: "test"
    }
  },
  {
    hostname: "^[A-Za-z0-9]+$",
    data: {
      environment: "internal"
    }
  },
  {
    hostname: "^160\\.6[78]\\.\\d+\\.\\d+$",
    data: {
      environment: "internal"
    }
  },
  {
    hostname: "^\\d+\\.\\d+\\.\\d+\\.\\d+$",
    data: {
      environment: "test"
    }
  },
  {
    hostname: "^nrkno\\.github\\.io$",
    data: {
      environment: "test"
    }
  }
];

// src/utils/site.ts
var ENVIRONMENT_EMBED_DIRECTLY = "embed-test";
var Site = class {
  hasParent;
  localSite;
  parentSite;
  constructor(config) {
    const {
      parentURL = document.referrer,
      localLocation = window.location,
      hasParent = window.top !== window.self
    } = config || {};
    this.hasParent = hasParent;
    this.localSite = this.detectSite(localLocation) || {};
    this.parentSite = {};
    if (hasParent && parentURL) {
      const parsedURL = this.parseURL(parentURL);
      if (parsedURL) {
        this.parentSite = this.detectSite(parsedURL);
      }
    }
  }
  get isEmbedded() {
    return !!(this.hasParent && this.localSite.isVideoEmbed);
  }
  get environment() {
    if (this.parentSite.environment) {
      return this.parentSite.environment;
    }
    if (this.localSite.environment) {
      return this.localSite.environment;
    }
    if (!this.hasParent && this.localSite.isVideoEmbed) {
      return ENVIRONMENT_EMBED_DIRECTLY;
    }
    return void 0;
  }
  get isProduction() {
    return !this.environment;
  }
  get name() {
    const name2 = this.parentSite.name || this.localSite.name;
    if (!name2 || !this.isProduction) {
      if (this.parentSite.location && this.parentSite.location.hostname) {
        return this.parentSite.location.hostname;
      }
      if (this.isEmbedded) {
        return "N/A";
      }
      if (this.localSite.location && this.localSite.location.hostname) {
        return this.localSite.location.hostname;
      }
      return "N/A";
    }
    return name2;
  }
  get fullName() {
    const tags = [];
    if (this.environment && (this.parentSite.configuredEnvironment || this.localSite.configuredEnvironment || this.environment === ENVIRONMENT_EMBED_DIRECTLY)) {
      tags.push(this.environment);
    }
    if (this.isEmbedded) {
      tags.push("embed");
    }
    if (tags.length) {
      return `${this.name} (${tags.join(", ")})`;
    }
    return this.name;
  }
  get options() {
    return this.parentSite.options || this.localSite.options || {};
  }
  parseURL(url) {
    return new URL(url);
  }
  detectSite(location) {
    const site = { location };
    const { hostname, pathname } = location;
    const data = this.matchSite(hostname, pathname);
    if (data) {
      Object.assign(site, data);
      if (site.environment) {
        site.configuredEnvironment = true;
      } else {
        const environment = this.matchEnvironment(hostname);
        if (environment) {
          site.environment = environment;
          site.options = void 0;
        }
      }
    }
    return site;
  }
  matchSite(hostname, pathname) {
    const siteConfig = siteConfigs.find((patterns) => {
      return new RegExp(patterns.hostname).test(hostname) && (patterns.pathname ? new RegExp(patterns.pathname).test(pathname) : true);
    });
    return siteConfig ? siteConfig.data : void 0;
  }
  matchEnvironment(hostname) {
    const match = /\b(dev|preprod|nonprod|stage|test)\b/.exec(hostname);
    return match ? match[1] : void 0;
  }
};

// src/integrations/player-core/index.ts
import { addLogHandler } from "@nrk/player-core/utils";

// src/tracking-apis/akamai-tracker.ts
var defaultAkamaiMediaAnalyticsURL = {
  http: "http://79423.analytics.edgesuite.net/ma_library/javascript/javascript_malibrary.js",
  https: "https://79423.analytics.edgekey.net/ma_library/javascript/javascript_malibrary.js"
};
function createAkamaiTracker(library, config, mediaURL) {
  const configPath = `${config.config.beacon}?enableGenericAPI=1`;
  const shouldSDKFetchManifest = false;
  window.AKAMAI_MEDIA_ANALYTICS_CONFIG_FILE_PATH = configPath;
  const tracker = new library(configPath);
  tracker.disableServerIPLookUp();
  tracker.disableLocation();
  tracker.setStreamURL(mediaURL, shouldSDKFetchManifest);
  [
    "title",
    "device",
    "deliveryType",
    "cdn",
    "cdnName",
    "category",
    "subCategory",
    "show",
    "contentLength",
    "playerId",
    "playerInfo"
  ].forEach((key) => tracker.setData(key, config.data[key]));
  return tracker;
}

// src/integrations/player-core/index.ts
import { getLogger as getLogger5 } from "@nrk/player-core/utils";

// src/tracking-apis/app-insights.ts
import { ApplicationInsights as ApplicationInsights2 } from "@microsoft/applicationinsights-web";
var InstrumentationKey = /* @__PURE__ */ ((InstrumentationKey2) => {
  InstrumentationKey2["Test"] = "d4e0f7d6-9fdb-42e9-b8f8-91ff288d01af";
  InstrumentationKey2["Stage"] = "f4cf2c52-4917-43bd-b257-d82b0a681e18";
  InstrumentationKey2["Prod"] = "ff70c9d2-d463-4e98-acb2-b1862bbddde1";
  return InstrumentationKey2;
})(InstrumentationKey || {});
function initAppInsights(key, config = {}) {
  const defaultConfig = {
    disableExceptionTracking: true,
    disableAjaxTracking: true,
    maxBatchInterval: 1e3,
    enableSessionStorageBuffer: false,
    isStorageUseDisabled: true,
    isRetryDisabled: true
  };
  const appInsights = new ApplicationInsights2({
    config: {
      instrumentationKey: key,
      ...defaultConfig,
      ...config
    }
  });
  appInsights.loadAppInsights();
  return appInsights;
}

// src/integrations/player-core/common-error-logging.ts
import { CoreEvent, PlaybackSessionErrorType } from "@nrk/player-core/types";
function logErrors(appTracker, session, info) {
  session.on(CoreEvent.Broken, (event) => {
    const error = event.error;
    if (!error) {
      return;
    }
    const mediaURL = session.getMediaInfo().url;
    const parsedMediaURL = new URL(mediaURL);
    const errorCategory = {
      [PlaybackSessionErrorType.PLAYBACK_STOPPED_DECODE_ERROR]: "media" /* Media */,
      [PlaybackSessionErrorType.PLAYBACK_STOPPED_NETWORK_ERROR]: "network" /* Network */,
      [PlaybackSessionErrorType.NO_PLAYBACK_NETWORK_ERROR]: "network" /* Network */,
      [PlaybackSessionErrorType.NO_PLAYBACK_DECODE_ERROR]: "media" /* Media */,
      [PlaybackSessionErrorType.UNKNOWN_ERROR]: "unknown" /* Unknown */,
      [PlaybackSessionErrorType.UNSUPPORTED_FORMAT_ERROR]: "media" /* Media */
    }[error.type];
    const isLive = session.isLiveStream();
    appTracker.trackEvent("nativePlayerError", {
      Properties: Object.assign(
        {
          mediaUrlHost: parsedMediaURL.host,
          mediaUrlPath: parsedMediaURL.pathname,
          mediaUrlQuery: parsedMediaURL.search,
          playerTime: `${Math.floor(
            isLive ? session.getCurrentTimeLive().getTime() / 1e3 : session.getCurrentTime()
          )}`,
          playerIsRunningInBackground: "n/a",
          streamingMode: isLive ? "live" : "onDemand",
          errorCategory,
          errorType: error.type,
          errorCode: "n/a",
          errorDescription: error.message,
          underlyingErrorType: error.causeSummary ?? "n/a",
          underlyingErrorCode: "n/a",
          underlyingErrorDescription: error.getCauseAsString()
        },
        info
      )
    });
  });
}

// src/integrations/player-core/akamai.ts
import { CoreEvent as CoreEvent2, PlayingChangeType, PausedReason, WaitingReason } from "@nrk/player-core/types";
import { abortable as abortable3 } from "@nrk/abortable";
import { getLogger as getLogger3 } from "@nrk/player-core/utils";
var logger3 = getLogger3("AkamaiTracker");
function trackAkamaiSession(session, tracker) {
  const abortController = new AbortController();
  let t = tracker;
  let isLive = false;
  const stopTracking = () => {
    t = void 0;
    abortController.abort();
    logger3.log("Akamai tracker stopped.");
  };
  session.on(CoreEvent2.PlayingChange, (event) => {
    if (event.isPlaying) {
      if (event.type === PlayingChangeType.Starting) {
        t?.handleSessionInit({
          streamHeadPosition: () => isLive ? session.getCurrentTimeLive().getTime() / 1e3 : session.getCurrentTime()
        });
        if (session.isTimeRunning()) {
          t?.handlePlaying();
        } else {
          session.once(CoreEvent2.TimeRunningChange, () => {
            t?.handlePlaying();
          });
        }
      } else {
        t?.handlePlaying();
      }
    } else if (event.pausedReason === PausedReason.Ended) {
      t?.handlePlayEnd("Play.End.Detected");
      stopTracking();
    } else {
      t?.handlePause();
    }
  });
  session.once(CoreEvent2.MediaInfoLoaded, () => {
    isLive = session.isLiveStream();
  });
  session.on(CoreEvent2.MediaRangeChange, (range) => {
    t?.setStreamDuration(range.end - range.start);
  });
  session.on(CoreEvent2.WaitingChange, (event) => {
    if (event.reason === WaitingReason.Seeking) {
      if (event.isWaiting) {
        t?.handleSeekStart();
      } else {
        t?.handleSeekEnd();
      }
    } else if (event.reason === WaitingReason.Rebuffering) {
      if (event.isWaiting) {
        t?.handleBufferStart();
      } else {
        t?.handleBufferEnd();
      }
    }
  });
  session.on(CoreEvent2.QualityChange, (quality) => {
    t?.handleBitRateSwitch(quality.bitrate);
  });
  session.on(CoreEvent2.Broken, (event) => {
    const error = event.error;
    t?.handleError(error ? `${error.causeSummary}: ${error.getCauseAsString()}` : "Unknown error");
    t?.handleApplicationExit();
    stopTracking();
  });
  session.on(CoreEvent2.SessionClosed, () => {
    t?.handleApplicationExit();
    stopTracking();
  });
  abortable3(
    beforeUnload(() => {
      t?.handleApplicationExit();
      stopTracking();
    }),
    abortController.signal
  );
}

// src/integrations/player-core/app-insights.ts
import { CoreEvent as CoreEvent3, PausedReason as PausedReason2 } from "@nrk/player-core/types";
import { abortable as abortable4, addEventListener as addEventListener2 } from "@nrk/abortable";
var STILL_WAITING_TIMEOUT = 20;
function trackAppInsightsSession(session, appTracker, destroySignal) {
  const eventPrefix = "player-core";
  let waiting;
  session.once(CoreEvent3.MediaInfoLoaded, () => {
    appTracker.trackEvent(`${eventPrefix}-media-info-loaded`);
  });
  session.on(CoreEvent3.PlayingChange, (event) => {
    if (event.pausedReason === PausedReason2.AutoplayBlocked) {
      appTracker.trackEvent(`${eventPrefix}-autoplay-blocked`);
    } else if (event.type !== void 0) {
      appTracker.trackEvent(`${eventPrefix}-${event.type}`);
    }
  });
  session.once(CoreEvent3.TimeRunningChange, (event) => {
    if (event.isTimeRunning) {
      appTracker.trackEvent(`${eventPrefix}-playing`);
    }
  });
  session.on(CoreEvent3.WaitingChange, (event) => {
    if (event.isWaiting) {
      if (waiting !== void 0) {
        waiting.stillWaitingTimeout.abort();
      }
      waiting = {
        reason: event.reason,
        startTime: Date.now(),
        stillWaitingTimeout: abortable4(
          setTimeout(() => {
            const eventName = `${eventPrefix}-duration-${event.reason}-${STILL_WAITING_TIMEOUT}s`;
            appTracker.trackEvent(eventName, { duration: STILL_WAITING_TIMEOUT * 1e3 });
          }, STILL_WAITING_TIMEOUT * 1e3),
          destroySignal
        )
      };
    } else {
      if (waiting?.reason !== event.reason) {
        waiting?.stillWaitingTimeout.abort();
        waiting = void 0;
        return;
      }
      const duration = Date.now() - waiting.startTime;
      const eventName = `${eventPrefix}-duration-${waiting.reason}`;
      waiting.stillWaitingTimeout.abort();
      waiting = void 0;
      appTracker.trackEvent(eventName, { duration });
    }
  });
  abortable4(
    [
      addEventListener2(document, "visibilitychange", () => {
        waiting?.stillWaitingTimeout.abort();
        waiting = void 0;
      }),
      addEventListener2(document, "webkitvisibilitychange", () => {
        waiting?.stillWaitingTimeout.abort();
        waiting = void 0;
      }),
      beforeUnload(() => {
        if (waiting !== void 0) {
          waiting?.stillWaitingTimeout.abort();
          const duration = Date.now() - waiting.startTime;
          appTracker.trackEvent(`${eventPrefix}-duration-${waiting.reason}-exit`, { duration });
        }
      })
    ],
    destroySignal
  );
  session.on(CoreEvent3.Broken, (event) => {
    const error = event.error;
    appTracker.trackEvent(`${eventPrefix}-broken`, { error });
    if (error) {
      appTracker.trackException(error, error.fatal, { cause: error.getCauseAsString() });
    }
  });
  session.once(CoreEvent3.SessionClosed, () => {
    appTracker.flush();
  });
}

// src/integrations/player-core/ecommerce.ts
import { CoreEvent as CoreEvent4 } from "@nrk/player-core/types";
function trackEcommerceSession(session, tracker, playedTimeCollector) {
  session.once(CoreEvent4.MediaRangeChange, (mediaRange) => {
    const STARTED_MIN_TIME = 120;
    playedTimeCollector.onceTotalPlayingTime(STARTED_MIN_TIME, () => {
      tracker.sendStartEvent();
    });
    playedTimeCollector.onceTotalPlayingTime(mediaRange.end * 0.9, () => {
      tracker.sendCompleteEvent();
    });
  });
}

// src/integrations/player-core/ga.ts
import { CoreEvent as CoreEvent5 } from "@nrk/player-core/types";
import { abortable as abortable5 } from "@nrk/abortable";
function trackGASession(session, playedTimeCollector, tracker, setup, destroySignal, epgEvents) {
  session.once(CoreEvent5.MediaInfoLoaded, (mediaInfo) => {
    tracker.updateFields({
      adapterType: session.getAdapterInfo().tech,
      mimeType: mediaInfo.mediaType?.type
    });
  });
  session.once(CoreEvent5.MediaRangeChange, (mediaRange) => {
    if (session.isLiveStream()) {
      const STARTED_MIN_TIME = 3;
      playedTimeCollector.onceTotalPlayingTime(STARTED_MIN_TIME, () => {
        tracker.sendStartedEvent(Math.round(playedTimeCollector.getTotalPlayingTime()));
      });
      const PROGRESS_INTERVAL = 6e5;
      let progressTimeout;
      let previousTotal = 0;
      const sendProgressEvent = () => {
        if (progressTimeout !== void 0) {
          progressTimeout.abort();
        }
        progressTimeout = abortable5(
          setTimeout(() => sendProgressEvent(), PROGRESS_INTERVAL),
          destroySignal
        );
        const scrubberValues = session.getScrubberValues();
        const timeBehindLive = scrubberValues.endPosition - scrubberValues.playheadPosition;
        const total = playedTimeCollector.getDurationOfMediaPlayed();
        const range = total - previousTotal;
        previousTotal = total;
        tracker.sendProgressEvent({ range, total, timeBehindLive });
      };
      epgEvents?.on("programchange", (entry) => {
        sendProgressEvent();
        tracker.updateFields({ programId: entry?.programId });
      });
      if (session.isTimeRunning()) {
        progressTimeout = abortable5(
          setTimeout(() => sendProgressEvent(), PROGRESS_INTERVAL),
          destroySignal
        );
      } else {
        session.once(CoreEvent5.TimeRunningChange, () => {
          progressTimeout = abortable5(
            setTimeout(() => sendProgressEvent(), PROGRESS_INTERVAL),
            destroySignal
          );
        });
      }
      session.on(CoreEvent5.Broken, () => {
        sendProgressEvent();
        if (progressTimeout !== void 0) {
          progressTimeout.abort();
          progressTimeout = void 0;
        }
      });
      session.on(CoreEvent5.SessionClosed, () => sendProgressEvent());
      abortable5(
        beforeUnload(() => sendProgressEvent()),
        destroySignal
      );
    } else {
      const duration = mediaRange.end;
      trackQuantiles(session, playedTimeCollector, tracker);
      playedTimeCollector.onceTotalPlayingTime(duration * 0.9, () => {
        tracker.sendAccumulatedCompleteEvent();
      });
      let sendContentConsumedEvent = () => {
        sendContentConsumedEvent = void 0;
        const playedTime = playedTimeCollector.getDurationOfMediaPlayed();
        if (playedTime > 0) {
          const percentConsumed = Math.round(playedTime / duration * 100);
          tracker.sendContentConsumedEvent(percentConsumed);
        }
      };
      session.on(CoreEvent5.SessionClosed, () => sendContentConsumedEvent?.());
      abortable5(
        beforeUnload(() => sendContentConsumedEvent?.()),
        destroySignal
      );
    }
  });
  setup.accumulatedTimeEvents?.forEach((seconds) => {
    playedTimeCollector.onceTotalPlayingTime(seconds, () => {
      tracker.sendAccumulatedSecondsEvent(seconds);
    });
  });
}
function trackQuantiles(session, playedTimeCollector, gaTracker) {
  const duration = session.getMediaRange().end;
  if (duration < 3) {
    return;
  }
  const started = new Promise((resolve) => {
    playedTimeCollector.onceTotalPlayingTime(3, () => {
      gaTracker.sendStartedEvent(Math.round(playedTimeCollector.getTotalPlayingTime()));
      resolve();
    });
  });
  const eventTrack = session.createTimeRangeEventTrack();
  eventTrack.on("enter", (event) => {
    eventTrack.removeRange(event.range);
    started.then(() => {
      const data = event.range.data;
      const playedTime = {
        range: Math.round(playedTimeCollector.getDurationOfMediaPlayed(data.targetRange)),
        total: Math.round(playedTimeCollector.getDurationOfMediaPlayed())
      };
      if (data.name === "complete") {
        gaTracker.sendCompletedEvent(playedTime);
      } else {
        gaTracker.sendQuantileEvent(data.name, playedTime);
      }
    }).catch((error) => console.error(error));
  });
  const range25 = { start: 0, end: duration * 0.25 };
  const range50 = { start: duration * 0.25, end: duration * 0.5 };
  const range75 = { start: duration * 0.5, end: duration * 0.75 };
  const range95 = { start: duration * 0.75, end: duration * 0.95 };
  const range100 = { start: duration * 0.95, end: duration + 1 };
  eventTrack.addRange(range50, {
    name: "media-playback-25" /* Quantile25 */,
    targetRange: range25
  });
  eventTrack.addRange(range75, {
    name: "media-playback-50" /* Quantile50 */,
    targetRange: range50
  });
  eventTrack.addRange(range95, {
    name: "media-playback-75" /* Quantile75 */,
    targetRange: range75
  });
  eventTrack.addRange(range100, {
    name: "complete",
    targetRange: range95
  });
}

// src/integrations/player-core/nielsen.ts
import { CoreEvent as CoreEvent7 } from "@nrk/player-core/types";
import { abortable as abortable6 } from "@nrk/abortable";

// src/tracking-apis/nielsen-sdk.ts
import cuid from "cuid";
var NIELSEN_NAMESPACE = "NOLBUNDLE";
var NielsenAppId = /* @__PURE__ */ ((NielsenAppId2) => {
  NielsenAppId2["Production"] = "P9540841F-01EA-4B24-9AC1-C079C8947927";
  NielsenAppId2["Test"] = "T9540841F-01EA-4B24-9AC1-C079C8947927";
  return NielsenAppId2;
})(NielsenAppId || {});
function enableNielsenSDK() {
  !function(t, n) {
    t[n] = t[n] || {
      nlsQ(e, o, c, r, s, i) {
        return s = t.document, r = s.createElement("script"), r.async = 1, r.src = ("http:" === t.location.protocol ? "http:" : "https:") + "//cdn-gl.imrworldwide.com/conf/" + e + ".js#name=" + o + "&ns=" + n, i = s.getElementsByTagName("script")[0], i.parentNode.insertBefore(r, i), t[n][o] = t[n][o] || {
          g: c || {},
          ggPM(e2, c2, r2, s2, i2) {
            (t[n][o].q = t[n][o].q || []).push([e2, c2, r2, s2, i2]);
          }
        }, t[n][o];
      }
    };
  }(window, NIELSEN_NAMESPACE);
}
var NielsenSDK = class {
  instance;
  constructor(appId, instanceName) {
    const name2 = instanceName ?? cuid();
    this.instance = window[NIELSEN_NAMESPACE].nlsQ(appId, name2);
  }
  loadMetadata(metadata) {
    this.instance.ggPM("loadMetadata", metadata);
  }
  setPlayheadPosition(time) {
    this.instance.ggPM("setPlayheadPosition", time);
  }
  stop(time) {
    this.instance.ggPM("stop", time);
  }
  end(time) {
    this.instance.ggPM("end", time);
  }
};

// src/integrations/player-core/nielsen.ts
import { getLogger as getLogger4 } from "@nrk/player-core/utils";

// src/utils/norwegian-day.ts
import EventEmitter from "eventemitter3";
import { CoreEvent as CoreEvent6, PlaybackState } from "@nrk/player-core/types";

// src/utils/time-with-tz.ts
var ROTATION = 60;
var RADIX = 10;
var pad = (n) => n < 10 ? `0${n}` : n;
var TimeWithTZ = class {
  utc;
  tzOffset;
  constructor(utc, tzOffset) {
    this.utc = utc;
    this.tzOffset = tzOffset;
  }
  static parse(timestamp) {
    const pattern = /^\/Date\((\d+)(?:([+-]\d{2})(\d{2}))?\)\/$/;
    const result = pattern.exec(timestamp);
    if (result) {
      const utc = parseInt(result[1], RADIX);
      const tzOffset = result.length > 2 ? parseInt(result[2], RADIX) * ROTATION + parseInt(result[3], RADIX) : 0;
      return new TimeWithTZ(utc, tzOffset);
    }
    throw new Error(`Unknown format: ${timestamp}`);
  }
  toModernPSAPIFormat() {
    const date = new Date(this.utc + this.tzOffset * ROTATION * 1e3);
    return [
      date.getUTCFullYear(),
      "-",
      pad(date.getUTCMonth() + 1),
      "-",
      pad(date.getUTCDate()),
      "T",
      pad(date.getUTCHours()),
      ":",
      pad(date.getUTCMinutes()),
      ":",
      pad(date.getUTCSeconds()),
      this.tzOffset < 0 ? "-" : "+",
      pad(Math.floor(this.tzOffset / ROTATION)),
      ":",
      pad(this.tzOffset % ROTATION)
    ].join("");
  }
  toCompactLocalDate() {
    const date = new Date(this.utc + this.tzOffset * ROTATION * 1e3);
    return [date.getUTCFullYear(), pad(date.getUTCMonth() + 1), pad(date.getUTCDate())].join("");
  }
  toDateObject() {
    return new Date(this.utc);
  }
};

// src/utils/norwegian-day.ts
import { isEuropeanSummerTime } from "@nrk/european-summer-time";
var NorwegianDayChangeTracker = class extends EventEmitter {
  constructor(session) {
    super();
    this.session = session;
    this.track = session.createTimeRangeEventTrack();
    this.track.on("enter", (event) => {
      const startPos = event.range.start;
      if (startPos !== this.currentDayStartPos) {
        if (this.currentDayStartPos !== void 0) {
          this.emit("daychange", event.range);
        }
        this.currentDayStartPos = startPos;
      }
    });
    if (session.getState() !== PlaybackState.Idle) {
      this.handleMediaRangeChange(session.getMediaRange());
    }
    session.on(CoreEvent6.MediaRangeChange, this.handleMediaRangeChange, this);
  }
  track;
  trackedFirstDayStart;
  trackedLastDayEnd;
  currentDayStartPos;
  destroy() {
    this.session.off(CoreEvent6.MediaRangeChange, this.handleMediaRangeChange, this);
    this.removeAllListeners();
    this.track.destroy();
    this.trackedFirstDayStart = void 0;
    this.trackedLastDayEnd = void 0;
    this.currentDayStartPos = void 0;
  }
  handleMediaRangeChange(mediaRange) {
    if (mediaRange.start === mediaRange.end || !Number.isFinite(mediaRange.end)) {
      return;
    }
    const fromTimestamp = this.session.positionToLiveTime(mediaRange.start);
    const toTimestamp = this.session.positionToLiveTime(mediaRange.end);
    const firstDayStart = getNorwegianStartOfDay(fromTimestamp);
    const lastDayEnd = getNorwegianStartOfDay(toTimestamp, 1);
    if (firstDayStart.utc === this.trackedFirstDayStart?.utc && lastDayEnd.utc === this.trackedLastDayEnd?.utc) {
      return;
    }
    this.track.removeAllRanges();
    let dayStart = firstDayStart;
    while (dayStart.utc !== lastDayEnd.utc) {
      const dayEnd = getNorwegianStartOfDay(dayStart, 1);
      this.track.addRange({
        start: this.session.liveTimeToPosition(dayStart.toDateObject()),
        end: this.session.liveTimeToPosition(dayEnd.toDateObject())
      });
      dayStart = dayEnd;
    }
    this.trackedFirstDayStart = firstDayStart;
    this.trackedLastDayEnd = lastDayEnd;
  }
};
function getNorwegianStartOfDay(timestamp, addDays) {
  const utc = typeof timestamp === "number" ? timestamp : timestamp instanceof Date ? timestamp.getTime() : timestamp.utc;
  const minuteInMs = 60 * 1e3;
  const dayInMs = 24 * 60 * 60 * 1e3;
  const tzOffsetTimestamp = getTimeZoneOffsetNorway(utc);
  const naiveNorwayTime = utc + tzOffsetTimestamp * minuteInMs + (addDays ?? 0) * dayInMs;
  const naiveNorwayStartOfDay = naiveNorwayTime - naiveNorwayTime % dayInMs;
  const tzOffsetStartOfDay = getTimeZoneOffsetNorway(naiveNorwayStartOfDay);
  const startOfDayUTC = naiveNorwayStartOfDay - tzOffsetStartOfDay * minuteInMs;
  return new TimeWithTZ(startOfDayUTC, tzOffsetStartOfDay);
}
function getTimeZoneOffsetNorway(timeUTC) {
  return isEuropeanSummerTime(timeUTC) ? 120 : 60;
}

// src/integrations/player-core/nielsen.ts
var logger4 = getLogger4("Nielsen");
var ACCEPTABLE_PROGRAM_CHANGE_DELAY = 0.9;
var MINIMUM_FETCH_WINDOW = 10;
function trackNielsenSession(session, psapiClient, { production = false, epgEvents }) {
  logger4.log("Waiting for media info loaded.");
  session.once(CoreEvent7.MediaInfoLoaded, (mediaInfo) => {
    if (mediaInfo.isAudioOnly) {
      psapiClient.loadNielsenConfig().then((metadata) => {
        if (metadata !== void 0) {
          logger4.debug("Got stream metadata:", metadata);
          enableNielsenSDK();
          logger4.debug("Nielsen SDK enabled.");
          const appId = production ? "P9540841F-01EA-4B24-9AC1-C079C8947927" /* Production */ : "T9540841F-01EA-4B24-9AC1-C079C8947927" /* Test */;
          const nielsen = new NielsenSDK(appId);
          if (epgEvents !== void 0) {
            new NielsenEPGSessionTracker(session, nielsen, metadata, epgEvents, psapiClient).start();
          } else {
            new NielsenSessionTracker(session, nielsen, metadata).start();
          }
        } else {
          logger4.error(`Manifest for ${psapiClient.manifest?.id} is missing Nielsen statistics URL.`);
        }
      }).catch((error) => {
        logger4.error("Failed to get Nielsen base metadata. No tracking.", error);
      });
    }
  });
}
var NielsenSessionTracker = class {
  destroyController;
  session;
  nielsen;
  metadata;
  getIntegerTime = () => 0;
  previousTransmittedTime = 0;
  isPlaying = false;
  constructor(session, nielsen, metadata) {
    this.session = session;
    this.nielsen = nielsen;
    this.metadata = this.filterMetadata(metadata);
  }
  start() {
    logger4.debug("Starting session tracker.");
    if (this.session.isLiveStream()) {
      this.getIntegerTime = () => Math.floor(this.session.getCurrentTimeLive().getTime() / 1e3);
    } else {
      this.getIntegerTime = () => Math.floor(this.session.getCurrentTime());
    }
    this.destroyController = abortable6(beforeUnload(() => this.nielsenEnd()));
    this.session.on(CoreEvent7.SessionClosed, () => this.nielsenEnd());
    this.session.on(CoreEvent7.TimeUpdate, () => this.nielsenTimeUpdate());
    this.session.on(CoreEvent7.PlayingChange, (event) => {
      if (event.isPlaying) {
        this.nielsenPlay();
      } else {
        this.nielsenStop();
      }
    });
    if (this.session.isPlaying()) {
      this.nielsenPlay();
    }
  }
  nielsenPlay() {
    this.nielsen.loadMetadata(this.metadata);
    this.isPlaying = true;
  }
  nielsenStop() {
    this.isPlaying = false;
    this.nielsen.stop(this.getIntegerTime());
  }
  nielsenEnd() {
    this.isPlaying = false;
    this.nielsen.end(this.getIntegerTime());
    this.destroyController?.abort();
  }
  nielsenTimeUpdate() {
    if (this.isPlaying) {
      const time = this.getIntegerTime();
      if (time !== this.previousTransmittedTime) {
        this.previousTransmittedTime = time;
        this.nielsen.setPlayheadPosition(time);
      }
    }
  }
  filterMetadata(metadata) {
    const data = { ...metadata };
    delete data.channelName;
    return data;
  }
};
var NielsenEPGSessionTracker = class extends NielsenSessionTracker {
  epgEvents;
  psapiClient;
  streamMetadata;
  dayChangeTracker;
  programMetadataCache = {};
  activeMetadataRequest = {};
  currentProgram;
  loadAndPlayController;
  constructor(session, nielsen, metadata, epgEvents, psapiClient) {
    super(session, nielsen, metadata);
    this.epgEvents = epgEvents;
    this.psapiClient = psapiClient;
    this.streamMetadata = this.filterMetadata(metadata);
    logger4.debug("Creating day change tracker.");
    this.dayChangeTracker = new NorwegianDayChangeTracker(session);
  }
  start() {
    logger4.debug("Starting EPG tracker.");
    this.epgEvents.on("programstartimminent", (entry, secondsLeft) => {
      const program = epgEntryToProgram(entry);
      logger4.log(`Program start imminent: ${program.programId} in ${secondsLeft.toFixed(3)} seconds.`);
      if (!(program.programId in this.programMetadataCache)) {
        const fetchDelay = Math.random() * Math.max(0, secondsLeft - MINIMUM_FETCH_WINDOW);
        if (fetchDelay > 0) {
          logger4.debug(`Requesting metadata in ${fetchDelay.toFixed(3)} seconds.`);
          abortable6(
            setTimeout(() => {
              if (!(program.programId in this.programMetadataCache)) {
                this.requestProgramMetadata(program);
              }
            }, Math.floor(fetchDelay * 1e3)),
            this.destroyController?.signal
          );
        } else {
          this.requestProgramMetadata(program);
        }
      }
    });
    this.epgEvents.on("programchange", (entry) => {
      const program = entry === void 0 ? void 0 : epgEntryToProgram(entry);
      if (this.currentProgram?.plannedStart.utc === program?.plannedStart.utc) {
        return;
      }
      this.currentProgram = program;
      logger4.log(`Program changed: ${program?.programId ?? "gap"}`);
      if (this.isPlaying) {
        this.nielsenStop();
        this.nielsenPlay();
      } else {
        if (program !== void 0 && !(program.programId in this.programMetadataCache)) {
          this.requestProgramMetadata(program);
        }
      }
    });
    this.epgEvents.on("epgupdated", (entries) => {
      logger4.log("EPG updated. Purging outdated metadata from cache.");
      const epgProgramIds = entries.map((entry) => entry.programId);
      Object.keys(this.programMetadataCache).filter((programIdInCache) => !epgProgramIds.includes(programIdInCache)).forEach((outdatedProgramId) => {
        delete this.programMetadataCache[outdatedProgramId];
      });
    });
    this.dayChangeTracker.on("daychange", () => {
      if (this.currentProgram === void 0 && this.isPlaying) {
        logger4.log("Day changed during gap, updating metadata for new day.");
        this.nielsenStop();
        this.nielsenPlay();
      }
    });
    super.start();
  }
  nielsenPlay() {
    const program = this.currentProgram;
    if (program === void 0) {
      this.metadata = this.getNoProgramMetadata(this.session.getCurrentTimeLive());
      super.nielsenPlay();
      logger4.log("Play with metadata for gap.");
      return;
    }
    const programId = program.programId;
    if (programId in this.programMetadataCache) {
      this.metadata = this.programMetadataCache[programId];
      super.nielsenPlay();
      logger4.log(`Play with prefetched metadata for ${programId}.`);
      return;
    }
    this.loadAndPlay(program).catch((e) => {
      logger4.error("Failed to load and play:", e);
    });
  }
  async loadAndPlay(program) {
    const programId = program.programId;
    let fallbackPlayCalled = false;
    this.metadata = this.getNoProgramMetadata(this.session.getCurrentTimeLive());
    const loadAndPlayController = abortable6(
      setTimeout(() => {
        fallbackPlayCalled = true;
        super.nielsenPlay();
        logger4.debug(`Play with fallback metadata while waiting for metadata for ${programId}`);
      }, ACCEPTABLE_PROGRAM_CHANGE_DELAY * 1e3),
      this.destroyController?.signal
    );
    this.loadAndPlayController = loadAndPlayController;
    this.requestProgramMetadata(program);
    await this.activeMetadataRequest[programId];
    if (loadAndPlayController.signal.aborted) {
      logger4.debug("Stop was called during metadata request. Aborting load and play.");
      return;
    }
    loadAndPlayController.abort();
    const programMetadata = this.programMetadataCache[programId];
    if (programMetadata !== void 0) {
      if (fallbackPlayCalled) {
        this.nielsenStop();
        logger4.debug("Replacing fallback metadata with program metadata.");
      } else {
        logger4.debug("Got program metadata before fallback applied.");
      }
      this.metadata = programMetadata;
      super.nielsenPlay();
    } else {
      logger4.debug("Failed to get program metadata. Using fallback metadata.");
      if (!fallbackPlayCalled) {
        super.nielsenPlay();
      }
    }
  }
  nielsenStop() {
    this.loadAndPlayController?.abort();
    super.nielsenStop();
  }
  nielsenEnd() {
    super.nielsenEnd();
    this.dayChangeTracker?.destroy();
  }
  requestProgramMetadata({ programId, plannedStart }) {
    if (!(programId in this.activeMetadataRequest)) {
      logger4.log(`Requesting metadata for ${programId}.`);
      this.activeMetadataRequest[programId] = this.psapiClient.loadNielsenConfig(plannedStart).then((programMetadata) => this.filterMetadata(programMetadata)).then((programMetadata) => {
        this.programMetadataCache[programId] = { ...this.streamMetadata, ...programMetadata };
        delete this.activeMetadataRequest[programId];
      }).catch((error) => {
        delete this.activeMetadataRequest[programId];
        logger4.error(`Failed to load metadata for ${programId}`, error);
      });
    }
  }
  getNoProgramMetadata(playheadLiveTime) {
    const dayStart = getNorwegianStartOfDay(playheadLiveTime);
    const dayEnd = getNorwegianStartOfDay(dayStart, 1);
    return {
      ...this.streamMetadata,
      airdate: dayStart.toModernPSAPIFormat(),
      scheduledEndDate: dayEnd.toModernPSAPIFormat()
    };
  }
};
function epgEntryToProgram(entry) {
  const { programId, plannedStart } = entry;
  return { programId, plannedStart };
}

// src/tracking-apis/scores-tracker.ts
var ScoresTracker = class {
  config;
  client;
  adapter;
  stream;
  programId;
  constructor(library, config, playerAdapter) {
    this.config = config;
    this.client = new library(config.springStreamSite || "test");
    this.adapter = this.createAdapter(playerAdapter);
    this.programId = config.springStreamProgramId;
  }
  createAdapter(playerAdapter) {
    return {
      getMeta: () => ({
        pl: playerAdapter.getPlayerName(),
        plv: playerAdapter.getPlayerVersion(),
        sx: window.screen.width,
        sy: window.screen.height
      }),
      getDuration: () => playerAdapter.isLive() ? 0 : Math.round(playerAdapter.getDuration()),
      getPosition: () => playerAdapter.isLive() ? playerAdapter.getCurrentLiveTime() : playerAdapter.getCurrentTime()
    };
  }
  createDescription() {
    return {
      stream: this.config.springStreamStream,
      ct: this.config.springStreamContentType,
      cq: this.programId?.toUpperCase()
    };
  }
  start() {
    if (!this.stream) {
      this.stream = this.client.track("nrk", this.createDescription(), this.adapter);
    }
  }
  stop() {
    if (this.stream) {
      this.stream.stop();
      this.stream = void 0;
    }
  }
  updateProgramId(programId) {
    if (this.programId !== programId) {
      this.programId = programId;
      if (this.stream) {
        this.stop();
        this.start();
      }
    }
  }
  get libraryVersion() {
    return this.client.version;
  }
};

// src/integrations/player-core/scores.ts
import { CoreEvent as CoreEvent8, PausedReason as PausedReason3 } from "@nrk/player-core/types";
import { abortable as abortable7 } from "@nrk/abortable";
function trackScoresSession(session, library, setup, destroySignal, epgEvents) {
  const adapter = createScoresAdapter(session, setup);
  const tracker = new ScoresTracker(library, setup.config, adapter);
  session.on(CoreEvent8.Broken, () => tracker.stop());
  session.on(CoreEvent8.SessionClosed, () => tracker.stop());
  abortable7(
    beforeUnload(() => tracker.stop()),
    destroySignal
  );
  epgEvents?.on("programchange", (entry) => tracker.updateProgramId(entry?.programId));
  session.on(CoreEvent8.PlayingChange, (event) => {
    if (event.isPlaying) {
      if (event.type !== void 0) {
        tracker.start();
      }
    } else {
      if (event.pausedReason === PausedReason3.Ended) {
        tracker.stop();
      }
    }
  });
  if (session.isPlaying()) {
    tracker.start();
  }
  return tracker;
}
function createScoresAdapter(session, setup) {
  return {
    getPlayerName: () => setup.playerName,
    getPlayerVersion: () => setup.playerVersion,
    isLive: () => session.isLiveStream(),
    getDuration: () => session.getMediaRange().end,
    getCurrentTime: () => session.getCurrentTime(),
    getCurrentLiveTime: () => session.getCurrentTimeLive().getTime() / 1e3
  };
}

// src/integrations/player-core/psapi-progress.ts
import { abortable as abortable8 } from "@nrk/abortable";
import { CoreEvent as CoreEvent9 } from "@nrk/player-core/types";
function trackSessionProgress(session, tracker, originalPlaybackStartTime, destroySignal) {
  let progressTimeout;
  let trackingEnabled = false;
  const reportingInterval = 3e4;
  const notBefore = 120;
  let playbackStartTime = originalPlaybackStartTime;
  const clearTimeout2 = () => {
    if (progressTimeout !== void 0) {
      progressTimeout.abort();
      progressTimeout = void 0;
    }
  };
  const sendProgressUpdate = () => {
    if (progressTimeout !== void 0) {
      progressTimeout.abort();
      progressTimeout = void 0;
    }
    if (reportingInterval > 0) {
      progressTimeout = abortable8(
        setTimeout(() => sendProgressUpdate(), reportingInterval),
        destroySignal
      );
    }
    if (trackingEnabled) {
      tracker.reportPlaybackProgress(playbackStartTime, session.getCurrentTime());
    }
  };
  session.once(CoreEvent9.MediaRangeChange, async (mediaRange) => {
    if (session.isLiveStream()) {
      return;
    }
    const eventTrack = session.createTimeRangeEventTrack();
    eventTrack.addRange({ start: notBefore, end: session.getScrubberRange().end });
    eventTrack.on("enter", (event) => {
      eventTrack.removeRange(event.range);
      trackingEnabled = true;
      sendProgressUpdate();
    });
    abortable8(
      beforeUnload(() => {
        if (trackingEnabled) {
          tracker.reportPlaybackProgress(playbackStartTime, session.getCurrentTime());
        }
        clearTimeout2();
      }),
      destroySignal
    );
  });
  session.on(CoreEvent9.WaitingChange, (event) => {
    if (!event.isWaiting && event.reason === "seeking") {
      playbackStartTime = session.getCurrentTime();
    }
  });
  session.on(CoreEvent9.TimeRunningChange, (event) => {
    if (trackingEnabled) {
      if (event.isTimeRunning) {
        clearTimeout2();
        progressTimeout = abortable8(
          setTimeout(() => sendProgressUpdate(), reportingInterval),
          destroySignal
        );
      } else {
        tracker.reportPlaybackProgress(playbackStartTime, session.getCurrentTime());
        clearTimeout2();
      }
    }
  });
  session.on(CoreEvent9.Broken, () => {
    if (trackingEnabled) {
      tracker.reportPlaybackProgress(playbackStartTime, session.getCurrentTime());
    }
    clearTimeout2();
  });
}

// src/integrations/player-core/index.ts
var logger5 = getLogger5("PlayerTracker");
var DEFAULT_PLAYER_FAMILY = "player-core";
var DEFAULT_PLAYER_PLATFORM = "web";
var sharedAppTracker;
var PlayerTracker = class {
  site;
  gaAppId;
  playerInfo;
  coreAnalyticsTracker;
  scoresLibrary;
  akamaiLibrary;
  appTracker;
  progressTracker;
  constructor(playerInfo, setup) {
    this.playerInfo = playerInfo;
    const { gaTrackingId, gaAppId, scoresLibrary, akamaiLibrary, parentURL } = setup;
    this.site = new Site(parentURL ? { parentURL } : void 0);
    this.gaAppId = gaAppId ?? "";
    if (gaTrackingId) {
      this.coreAnalyticsTracker = createCoreAnalyticsTracker(gaTrackingId, playerInfo.name);
    }
    this.scoresLibrary = scoresLibrary;
    this.akamaiLibrary = akamaiLibrary;
    this.appTracker = createAppTracker(this.site, playerInfo, parentURL);
    if (sharedAppTracker === void 0) {
      logger5.log("Creating shared app tracker for log tracking.");
      sharedAppTracker = createAppTracker(this.site, playerInfo, parentURL);
      addLogHandler(new AppInsightsLogHandler(sharedAppTracker));
    }
  }
  trackSession(session, psapiClient, config, epgEvents) {
    const sessionId = cuid2();
    const destroyController = new AbortController();
    const playedTimeCollector = createPlayedTimeCollector(session);
    const trackingSession = { playedTimeCollector };
    let scoresTracker;
    session.on(CoreEvent10.SessionClosed, () => {
      destroyController.abort();
      playedTimeCollector.destroy();
    });
    if (config.ga && this.coreAnalyticsTracker) {
      logger5.log("Tracking session with GA.");
      const gaTracker = new GATracker(
        this.coreAnalyticsTracker,
        { appId: this.gaAppId, appName: this.playerInfo.name, appVersion: this.playerInfo.version },
        config.ga.fields
      );
      trackGASession(session, playedTimeCollector, gaTracker, config.ga, destroyController.signal, epgEvents);
      trackingSession.gaSender = gaTracker;
    }
    trackEcommerceSession(session, config.ecommerce.tracker, playedTimeCollector);
    if (config.progress) {
      logger5.log("Tracking session with PSAPI progress tracker.");
      this.progressTracker = new ProgressTracker(
        config.progress.programId,
        config.progress.playbackStartTime,
        config.progress.trackingCallback
      );
      trackSessionProgress(session, this.progressTracker, config.progress.playbackStartTime, destroyController.signal);
    }
    if (config.scores && this.scoresLibrary) {
      logger5.log("Tracking session with TNS Scores.");
      scoresTracker = trackScoresSession(
        session,
        this.scoresLibrary,
        {
          playerName: this.playerInfo.name,
          playerVersion: this.playerInfo.version,
          config: config.scores.config
        },
        destroyController.signal,
        epgEvents
      );
    }
    if (config.akamai && this.akamaiLibrary) {
      logger5.log("Tracking session with Akamai/Luna.");
      const mediaURL = session.getMediaInfo().url;
      const akamaiTracker = createAkamaiTracker(
        this.akamaiLibrary,
        {
          data: { ...config.akamai.config.data, playerId: this.playerInfo.name },
          config: config.akamai.config.config
        },
        mediaURL
      );
      trackAkamaiSession(session, akamaiTracker);
    }
    logger5.log("Tracking session with Nielsen (if audio only).");
    trackNielsenSession(session, psapiClient, { production: this.site.isProduction, epgEvents });
    logger5.log("Tracking session with App Insights.");
    const appInsightsSessionProps = () => {
      const timestamp = session.getCurrentTimeLive();
      const time = timestamp !== void 0 ? timestamp.getTime() / 1e3 : session.getCurrentTime();
      return {
        trackingSession: sessionId,
        mediaInfo: session.getMediaInfo(),
        adapterInfo: session.getAdapterInfo(),
        documentHidden: "webkitHidden" in document ? document.webkitHidden : document.hidden,
        versions: {
          PlayerCore: playerCoreVersion,
          SpringStreams: scoresTracker?.libraryVersion ?? "none",
          CoreAnalytics: this.coreAnalyticsTracker?.version ?? "none"
        },
        playback: {
          time,
          position: session.getCurrentTime(),
          quality: session.getCurrentQuality(),
          rate: session.getPlaybackRate(),
          state: session.getState(),
          waitingReason: session.getWaitingReason(),
          pausedReason: session.getPausedReason(),
          mediaRange: session.getMediaRange()
        },
        displayModes: {
          pip: session.getPiPState(),
          fullscreen: session.getFullscreenState(),
          remote: session.getRemotePlaybackState()
        }
      };
    };
    this.appTracker.addPropsCallback(appInsightsSessionProps);
    session.once(CoreEvent10.SessionClosed, () => this.appTracker.removePropsCallback(appInsightsSessionProps));
    trackAppInsightsSession(session, this.appTracker, destroyController.signal);
    if (config.metadata) {
      logger5.log("Tracking playback errors.");
      const playerInfo = this.playerInfo;
      logErrors(this.appTracker, session, {
        platform: playerInfo.platform ?? DEFAULT_PLAYER_PLATFORM,
        applicationName: playerInfo.name,
        applicationVersion: playerInfo.version,
        ...config.metadata
      });
    }
    return trackingSession;
  }
  destroy() {
    this.appTracker.destroy();
  }
};
function createPlayedTimeCollector(session) {
  const playedTimeCollector = new PlayedTimeCollector();
  session.on(CoreEvent10.TimeRunningChange, (event) => {
    if (event.isTimeRunning) {
      playedTimeCollector.timeStarted(event.time);
    } else {
      playedTimeCollector.timeStopped(event.time);
    }
  });
  session.on(CoreEvent10.TimeUpdate, (time) => {
    playedTimeCollector.timeUpdate(time);
  });
  return playedTimeCollector;
}
function createCoreAnalyticsTracker(gaTrackingId, name2, debug) {
  const safeName = name2.replace(/[^\w]/g, "_").replace(/__+/g, "_").toLowerCase();
  const trackerName = `${safeName}_${cuid2.slug()}`;
  logger5.log("Creating CoreAnalytics tracker with name:", trackerName);
  const tracker = coreAnalytics.create();
  tracker("initialize", {
    ga: gaTrackingId,
    trackerName,
    debug
  });
  return tracker;
}
function createAppTracker(site, playerInfo, parentURL) {
  const instrumentationKey = site.isProduction ? "ff70c9d2-d463-4e98-acb2-b1862bbddde1" /* Prod */ : "f4cf2c52-4917-43bd-b257-d82b0a681e18" /* Stage */;
  const siteOptions = site.options.applicationInsights;
  const appInsights = initAppInsights(instrumentationKey, siteOptions);
  logger5.log("Site:", site.fullName);
  const customProperties = {
    applicationName: playerInfo.name,
    applicationVersion: playerInfo.version,
    applicationFamily: playerInfo.family ?? DEFAULT_PLAYER_FAMILY,
    hostname: window.location.hostname,
    site: site.fullName,
    parentURL
  };
  return new AppTracker(appInsights, customProperties);
}

// src/utils/epg-events.ts
import EventEmitter2 from "eventemitter3";
import { CoreEvent as CoreEvent11 } from "@nrk/player-core/types";
import { getLogger as getLogger6 } from "@nrk/player-core/utils";
var logger6 = getLogger6("EPGEvents");
var NON_INDEX = -1;
var IMMIMENT_PERIOD = 180;
var EPGEvents = class extends EventEmitter2 {
  session;
  changeTrack;
  imminentTrack;
  updating = false;
  programExitTimeout;
  pausedEPGIndex;
  currentEPGIndex;
  entries;
  constructor(session) {
    super();
    this.session = session;
    this.session.on(CoreEvent11.TimeRunningChange, this.handleTimeRunningChange, this);
    this.changeTrack = session.createTimeRangeEventTrack();
    this.changeTrack.on("enter", this.handleProgramChange, this);
    this.changeTrack.on("exit", this.handleProgramChange, this);
    this.imminentTrack = session.createTimeRangeEventTrack();
    this.imminentTrack.on("enter", this.handleProgramStartImminent, this);
    this.entries = [];
  }
  getCurrentEntry() {
    return this.currentEPGIndex !== void 0 ? this.entries[this.currentEPGIndex] : void 0;
  }
  async updateEPG(entries) {
    let timeRange = this.session.getMediaRange();
    if (timeRange.start === 0 && timeRange.end === 0) {
      logger6.log("Waiting for media range.");
      await new Promise((resolve) => this.session.once(CoreEvent11.MediaRangeChange, () => resolve()));
      timeRange = this.session.getMediaRange();
    }
    logger6.log("Updating EPG data.");
    this.updating = true;
    this.changeTrack.removeAllRanges();
    this.imminentTrack.removeAllRanges();
    this.entries = entries;
    for (let i = 0; i < entries.length; ++i) {
      const program = entries[i];
      const programEndPosition = this.session.liveTimeToPosition(program.actualEnd.toDateObject());
      if (programEndPosition > timeRange.start) {
        const programStartPosition = this.session.liveTimeToPosition(program.actualStart.toDateObject());
        this.changeTrack.addRange({ start: programStartPosition, end: programEndPosition }, i);
        this.imminentTrack.addRange({ start: programStartPosition - IMMIMENT_PERIOD, end: programStartPosition }, i);
      }
    }
    this.updating = false;
    this.emit("epgupdated", entries);
    logger6.log("EPG update done.");
  }
  destroy() {
    if (this.programExitTimeout !== void 0) {
      clearTimeout(this.programExitTimeout);
      this.programExitTimeout = void 0;
    }
    this.changeTrack.destroy();
    this.imminentTrack.destroy();
    this.removeAllListeners();
  }
  handleTimeRunningChange(event) {
    if (event.isTimeRunning && this.pausedEPGIndex !== void 0) {
      const epgIndex = this.pausedEPGIndex === NON_INDEX ? void 0 : this.pausedEPGIndex;
      this.pausedEPGIndex = void 0;
      if (this.currentEPGIndex !== epgIndex) {
        this.changeProgram(epgIndex);
      }
    }
  }
  handleProgramChange(event) {
    if (this.programExitTimeout !== void 0) {
      clearTimeout(this.programExitTimeout);
      this.programExitTimeout = void 0;
    }
    const epgIndex = event.range.data;
    if (event.type === "enter" && this.updating && epgIndex === this.currentEPGIndex) {
      logger6.debug(`Got ${event.type} ${epgIndex}. Ignoring due to data update.`);
      return;
    }
    if (this.session.isTimeRunning()) {
      if (event.type === "enter") {
        logger6.debug(`Got ${event.type} ${epgIndex}. Changing to ${epgIndex}.`);
        this.changeProgram(epgIndex);
      } else if (this.currentEPGIndex === epgIndex) {
        logger6.debug(`Got ${event.type} ${epgIndex}. Changing to no program unless immediate enter.`);
        this.changeProgram(void 0);
      } else {
        logger6.debug(`Got ${event.type} ${epgIndex}. Ignoring due to late exit.`);
      }
    } else {
      if (event.type === "enter") {
        logger6.debug(`Got ${event.type} ${epgIndex}. Changing to ${epgIndex} when time starts.`);
        this.pausedEPGIndex = epgIndex;
        this.emit("programstartimminent", this.entries[epgIndex], 0);
      } else if (this.pausedEPGIndex === void 0 && this.currentEPGIndex === epgIndex) {
        logger6.debug(`Got ${event.type} ${epgIndex}. Changing to no program when time starts.`);
        this.pausedEPGIndex = NON_INDEX;
      } else if (this.pausedEPGIndex === epgIndex) {
        logger6.debug(`Got ${event.type} ${epgIndex}. Clearing next program.`);
        this.pausedEPGIndex = void 0;
      } else {
        logger6.debug(`Got ${event.type} ${epgIndex}. Ignoring due to late exit.`);
      }
    }
  }
  handleProgramStartImminent(event) {
    const epgIndex = event.range.data;
    const programStart = event.range.end;
    const currentTime = this.session.getCurrentTime();
    this.emit("programstartimminent", this.entries[epgIndex], Math.max(0, programStart - currentTime));
  }
  changeProgram(epgIndex) {
    if (epgIndex !== void 0) {
      this.currentEPGIndex = epgIndex;
      const currentEntry = this.getCurrentEntry();
      logger6.log(`Program changed: ${currentEntry?.programId} - ${currentEntry?.title}`);
      this.emit("programchange", currentEntry);
    } else {
      this.programExitTimeout = setTimeout(() => {
        this.currentEPGIndex = void 0;
        logger6.log("Program changed: No program.");
        this.emit("programchange");
      }, 100);
    }
  }
};
export {
  AppTracker,
  DimensionMap,
  EPGEvents,
  GASender,
  GATracker,
  InstrumentationKey,
  MetricMap,
  NielsenAppId,
  NielsenSDK,
  NorwegianDayChangeTracker,
  PlayedTimeCollector,
  PlayerTracker,
  ProgressTracker,
  Quantile,
  ScoresTracker,
  Site,
  clipRanges,
  createAkamaiTracker,
  defaultAkamaiMediaAnalyticsURL,
  enableNielsenSDK,
  getNorwegianStartOfDay,
  getTimeZoneOffsetNorway,
  initAppInsights,
  mergeOverlappingRanges,
  siteConfigs,
  summarizeRanges
};
